import {
  Callout,
  DuplicateStoreConnectionMeta,
  P,
  SystemProblem,
} from '@vgrubs/components';
import { problemGuide } from '../helpers/systemProblemGuide';

/**
 * A duplicate: whether anyone still has to disconnect a store, which depends
 * on what the backend managed to do itself (`meta.action`).
 */
const DuplicateNextStep = ({
  problem,
  meta,
}: {
  problem: SystemProblem;
  meta: DuplicateStoreConnectionMeta;
}) => {
  const brand = problem.brandName ?? 'the newer brand';

  if (meta.action === 'disconnected') {
    return (
      <Callout tone="warning">
        <P $m="0" $left>
          The store was disconnected from {brand} automatically, because that
          connection came last. The older brand kept it. Nothing is left to
          disconnect - check it is the right way round and mark it resolved.
        </P>
      </Callout>
    );
  }

  return (
    <Callout tone="danger">
      <P $m="0" $left $bold>
        Still connected to both brands - disconnect it from one by hand.
      </P>
      <P $m="4px 0 0" $left>
        {meta.action === 'disconnectFailed'
          ? `vGrubs tried to disconnect it from ${brand}, but POS Hub refused.`
          : 'KitchenHub duplicates are never disconnected automatically.'}{' '}
        Mark it resolved only once one of the connections is gone.
        {meta.source === 'kitchenhub' &&
          ' If both are still there at the next nightly sync, a new problem opens.'}
      </P>
    </Callout>
  );
};

/**
 * What support has to do about an open problem, in a sentence, and whether it
 * will close by itself - so nobody presses Resolve on something the system is
 * about to close, or waits for one it never will. Tinted by severity. The
 * backend's description says what happened; this says what is left.
 */
export const ProblemNextStep = ({ problem }: { problem: SystemProblem }) => {
  if (problem.type === 'duplicateStoreConnection' && problem.meta) {
    return (
      <DuplicateNextStep
        problem={problem}
        meta={problem.meta as DuplicateStoreConnectionMeta}
      />
    );
  }

  const guide = problemGuide(problem);
  if (!guide) return null;

  const tone =
    problem.severity === 'critical'
      ? 'danger'
      : problem.severity === 'warning'
      ? 'warning'
      : 'info';

  return (
    <Callout tone={tone}>
      <P $m="0" $left $bold>
        {guide.todo}
      </P>
      <P $m="4px 0 0" $left>
        {guide.closes}
      </P>
    </Callout>
  );
};
